import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { isWorkspacePath } from '@/lib/workspaceNav';
import { useAuth } from '@/lib/auth';
import { NAV, AUTH } from '@/constants/testIds';
import { FEATURES } from '@/config/features';
import { Menu, X, HardHat, ChevronDown, LayoutGrid, Settings, CreditCard, MessageSquare, Info, ShieldCheck, LogOut, BookOpen, Fuel, Bot, Store, Car, Truck } from 'lucide-react';
import { useState } from 'react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

const RESOURCES = [
  { to: '/guides', label: 'Guides', desc: 'How-to & pricing guides', Icon: BookOpen, tid: 'nav-guides' },
  { to: '/fuel', label: 'Fuel Prices', desc: 'Cheapest diesel near you', Icon: Fuel, tid: 'nav-fuel' },
  { to: '/plant-hire', label: 'Plant Hire', desc: 'Find plant & tool hire nearby', Icon: Truck, tid: 'nav-resources-plant-hire' },
  { to: '/assistant', label: 'AI Assistant', desc: 'Ask anything trade-related', Icon: Bot, tid: NAV.assistant },
  ...(FEATURES.marketplace ? [{ to: '/marketplace', label: 'Marketplace', desc: 'Buy & sell', Icon: Store, tid: 'nav-marketplace' }] : []),
  ...(FEATURES.vehicleCheck ? [{ to: '/vehicle-check', label: 'Vehicle Check', desc: 'MOT & tax lookup', Icon: Car, tid: 'nav-vehicle' }] : []),
];
const MAIN = [
  { to: '/calculators', label: 'Calculators', tid: NAV.trades },
  { to: '/pricing', label: 'Pricing', tid: 'nav-pricing' },
];
const linkCls = ({ isActive }) => `relative whitespace-nowrap px-3 py-2 text-[13.5px] font-medium transition-colors ${isActive ? 'text-white' : 'text-slate-300 hover:text-white'}`;
const itemCls = 'cursor-pointer gap-3 py-2 focus:bg-slate-100 focus:text-slate-900';

function AccountMenu({ user, onLogout }) {
  const nav = useNavigate();
  const initials = (user.name || user.email || '?').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
  const go = (to) => () => nav(to);
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger data-testid="nav-account-btn" className="flex items-center gap-2 rounded-full pl-1 pr-2.5 py-1 border border-white/15 hover:border-white/40 transition-colors outline-none">
        <span className="w-7 h-7 rounded-full bg-white/10 text-white text-[11px] font-bold grid place-items-center">{initials}</span>
        <ChevronDown size={14} className="text-slate-300" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60" data-testid="nav-account-menu">
        <DropdownMenuLabel className="font-normal">
          <p className="text-sm font-semibold text-slate-900 truncate" data-testid="nav-user-name">{user.name || 'Account'}</p>
          <p className="text-xs text-slate-500 truncate">{user.email}</p>
          {(user.is_pro_plus || user.is_pro) && <span className="tm-chip tm-chip-orange mt-1.5" data-testid={user.is_pro_plus ? 'nav-pro-plus-badge' : 'nav-pro-badge'}>{user.is_pro_plus ? 'PRO+' : 'PRO'}</span>}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={go('/workspace')} className={itemCls} data-testid="nav-acc-workspace"><LayoutGrid size={15} /> My Workspace</DropdownMenuItem>
        <DropdownMenuItem onSelect={go('/settings')} className={itemCls} data-testid="nav-settings"><Settings size={15} /> Account & settings</DropdownMenuItem>
        <DropdownMenuItem onSelect={go('/pricing')} className={itemCls} data-testid="nav-acc-subscription"><CreditCard size={15} /> Subscription</DropdownMenuItem>
        {user.role === 'admin' && <DropdownMenuItem onSelect={go('/admin')} className={itemCls} data-testid="nav-admin"><ShieldCheck size={15} /> Admin</DropdownMenuItem>}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={go('/feedback')} className={itemCls} data-testid="nav-feedback"><MessageSquare size={15} /> Feedback & ideas</DropdownMenuItem>
        <DropdownMenuItem onSelect={go('/about')} className={itemCls} data-testid="nav-about"><Info size={15} /> About TradesMate</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onLogout} className={`${itemCls} text-rose-600`} data-testid={AUTH.logoutBtn}><LogOut size={15} /> Log out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ResourcesMenu() {
  const nav = useNavigate();
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger data-testid="nav-resources" className="flex items-center gap-1 px-3 py-2 text-[13.5px] font-medium text-slate-300 hover:text-white outline-none data-[state=open]:text-white">
        Resources <ChevronDown size={14} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64 p-1.5" data-testid="nav-resources-menu">
        {RESOURCES.map((r) => (
          <DropdownMenuItem key={r.to} onSelect={() => nav(r.to)} className={`${itemCls} items-start`} data-testid={r.tid}>
            <span className="w-8 h-8 rounded-lg bg-brand/10 text-brand grid place-items-center shrink-0"><r.Icon size={15} /></span>
            <span><span className="block text-sm font-semibold text-slate-900">{r.label}</span><span className="block text-xs text-slate-500">{r.desc}</span></span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const { pathname } = useLocation();
  const wide = user && isWorkspacePath(pathname);
  const doLogout = async () => { await logout(); setOpen(false); nav('/'); };
  const mobileLinks = [...MAIN, ...RESOURCES, { to: '/about', label: 'About', tid: 'nav-about' }, { to: '/feedback', label: 'Feedback', tid: 'nav-feedback' }];

  return (
    <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-xl text-white border-b border-white/10">
      <div className={`${wide ? 'px-4 lg:px-5' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'} h-16 flex items-center justify-between`}>
        <Link to="/" data-testid={NAV.brand} className="flex items-center gap-2.5 group">
          <span className="w-8 h-8 rounded-full bg-brand text-brand-ink grid place-items-center ring-1 ring-white/20 transition-transform duration-300 group-hover:rotate-[-8deg]">
            <HardHat size={16} strokeWidth={2} />
          </span>
          <span className="font-display text-xl font-semibold tracking-tight">TradesMate</span>
          <span className="hidden lg:inline text-[10px] font-semibold uppercase tracking-[0.3em] text-gold">UK</span>
        </Link>
        <nav className="hidden md:flex items-center gap-1" data-testid="nav-main">
          <NavLink to={MAIN[0].to} data-testid={MAIN[0].tid} className={linkCls}>{MAIN[0].label}</NavLink>
          <ResourcesMenu />
          {MAIN.slice(1).map((l) => <NavLink key={l.to} to={l.to} data-testid={l.tid} className={linkCls}>{l.label}</NavLink>)}
          <div className="ml-3 flex items-center gap-2">
            {user ? (
              <>
                <Link to="/workspace" data-testid="nav-workspace" title="My Workspace" className="tm-btn whitespace-nowrap text-xs px-3 lg:px-4 py-2 min-h-0"><LayoutGrid size={14} /><span className="hidden lg:inline">My Workspace</span></Link>
                <AccountMenu user={user} onLogout={doLogout} />
              </>
            ) : (
              <>
                <Link to="/login" data-testid={NAV.loginCta} className="text-[13.5px] font-medium text-slate-300 hover:text-white px-3 py-2">Log in</Link>
                <Link to="/register" data-testid="nav-register" className="tm-btn text-xs px-4 py-2 min-h-0">Start free</Link>
              </>
            )}
          </div>
        </nav>
        <button className="md:hidden text-white" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="nav-mobile-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-white/10 bg-slate-900 px-4 py-3 space-y-1" data-testid="nav-mobile-menu">
          {user && <Link to="/workspace" onClick={() => setOpen(false)} data-testid="nav-workspace-mobile" className="tm-btn w-full text-sm mb-2"><LayoutGrid size={15} /> My Workspace</Link>}
          {mobileLinks.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`${l.tid}-mobile`} onClick={() => setOpen(false)} className="block py-2 font-medium text-slate-200">{l.label}</NavLink>
          ))}
          {user?.role === 'admin' && <NavLink to="/admin" onClick={() => setOpen(false)} data-testid="nav-admin-mobile" className="block py-2 font-medium text-slate-200">Admin</NavLink>}
          {user && <NavLink to="/settings" data-testid="nav-settings-mobile" onClick={() => setOpen(false)} className="block py-2 font-medium text-slate-200">Account & settings</NavLink>}
          {user ? (
            <button data-testid={`${AUTH.logoutBtn}-mobile`} onClick={doLogout} className="block py-2 text-sm text-slate-300">Log out</button>
          ) : (
            <Link to="/login" onClick={() => setOpen(false)} className="tm-btn text-xs w-full">Log in</Link>
          )}
        </div>
      )}
      <div className="h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent" />
    </header>
  );
}
